import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductCopyDetails } from '../../../store/api';
import { DataTable } from '../../../shared/common/DataTable';
import { THEME_SECONDARY, themeColor } from '../../../styles/basics/colors';
import { ProductUsageConditions } from '../../../shared/product/ProductUsageConditions';
import LoadingOverlay from '../../../shared/components/LoadingOverlay';
import { invalidateProductCopies, useProductCopies } from '../../../shop/cart/api/useProductCopies';
import { CopyDetailsErrorMessage } from './CopyDetailsErrorMessage';
import isEmpty from 'lodash/isEmpty';
import { mapCollectionType, mapStatus } from './utils';
import { CopyDetailsPopupSorting } from './popupDefaultSorting';
import './CopyDetailsPopup.scss';
import { LoanFormatRequestKey } from '../../../../models/LoanFormat';
import styled from 'styled-components';

export const StDivNoCopies = styled.div<{ tableMaxHeight: number }>`
    height: ${props => props.tableMaxHeight}px;
`;

interface Props {
    tableMaxHeight: number;
    loanFormatKey: LoanFormatRequestKey;
    productId: string;
    currentSorting: CopyDetailsPopupSorting;
    sortByComponent: JSX.Element;
    isLocalContentVariant: boolean;
    shopLanguage: string | undefined;
}

export const ProductCopyDetailsTable: FC<Props> = ({
    tableMaxHeight,
    loanFormatKey,
    productId,
    currentSorting,
    sortByComponent,
    isLocalContentVariant,
    shopLanguage
}) => {
    const { t } = useTranslation();

    const { data: copiesList, isInitialLoading: isProductCopiesLoading, isError } = useProductCopies(productId);

    const copies = copiesList || [];

    if (isProductCopiesLoading) {
        return <LoadingOverlay />;
    }

    if (isError) {
        return <CopyDetailsErrorMessage retry={() => invalidateProductCopies(productId)} />;
    }

    if (isEmpty(copies)) {
        return (
            <StDivNoCopies tableMaxHeight={tableMaxHeight}>
                <p className="centered">{t('components.copyDetailsPopup.noCopies')}</p>
            </StDivNoCopies>
        );
    }

    copies.sort(currentSorting.comparator);

    return (
        <div className="copy-details-popup">
            <div className="copy-list-header">
                <h4> {t('components.copyDetailsPopup.copyList')}</h4>
                {sortByComponent}
            </div>
            <DataTable
                data={copies}
                cellPaddingVertical={10}
                cellPaddingHorizontal={5}
                maxHeight={tableMaxHeight}
                minHeight={tableMaxHeight}
                getRowStyle={copy => ({
                    color: copy.status === 'Expired' ? themeColor(THEME_SECONDARY) : undefined
                })}
                columns={[
                    ...(!isLocalContentVariant
                        ? [
                              {
                                  label: t('components.copyDetailsPopup.order'),
                                  accessor: copy => copy.orderNumber || '-'
                              },
                              { label: t('components.copyDetailsPopup.invoice'), accessor: copy => copy.invoice || '-' }
                          ]
                        : []),
                    {
                        label: t('components.copyDetailsPopup.shipDate'),
                        accessor: copy =>
                            copy.shipDate
                                ? t('dateFormat.date', {
                                      val: new Date(copy.shipDate)
                                  })
                                : '-'
                    },
                    {
                        label: t('components.copyDetailsPopup.startDate'),
                        accessor: copy =>
                            copy.startDate
                                ? t('dateFormat.date', {
                                      val: new Date(copy.startDate)
                                  })
                                : '-'
                    },
                    {
                        label: t('components.copyDetailsPopup.expiryDate'),
                        accessor: copy =>
                            copy.expiryDate
                                ? t('dateFormat.date', {
                                      val: new Date(copy.expiryDate)
                                  })
                                : '-'
                    },
                    { label: t('components.copyDetailsPopup.usageCount'), accessor: 'usageCount' },
                    {
                        label: t('components.copyDetailsPopup.usageConditions'),
                        accessor: (copy: ProductCopyDetails) => (
                            <ProductUsageConditions
                                showDetails={true}
                                product={copy}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={shopLanguage}
                            />
                        )
                    },
                    {
                        label: t('components.copyDetailsPopup.collection'),
                        accessor: (copy: ProductCopyDetails) =>
                            copy.collectionType && copy.collection ? (
                                <>
                                    {mapCollectionType(copy.collectionType, t)}
                                    <br />
                                    {copy.collection}
                                </>
                            ) : (
                                '-'
                            )
                    },
                    {
                        label: t('components.copyDetailsPopup.status'),
                        accessor: copy => mapStatus(copy.status ?? '', t)
                    }
                ]}
            />
        </div>
    );
};
